/*
 * ============================================================
 *  INVIZI BRAND — OKENDO DOM RESTRUCTURE
 *  File: invizi_brand_okendo.js
 *
 *  Handles Okendo's two-phase render:
 *    Phase 1 – initial paint (header + bars visible)
 *    Phase 2 – Okendo re-renders after fetching live data
 *              (replaces inner DOM, wiping our injections)
 *
 *  Strategy:
 *    • Run restructure() on first detection
 *    • Watch .oke-w-header-content with a MutationObserver
 *    • If our .oke-brand-scorePanel disappears → re-inject
 * ============================================================
 */

(function () {
  'use strict';

  const ACCENT      = '#c97b5a';
  const ACCENT_MUTE = 'rgba(201,123,90,0.22)';
  const POLL_MS     = 100;
  const POLL_MAX    = 20000;

  /* ── Star builder ────────────────────────────────────────── */
  function buildStars(rating, uid) {
    const floor   = Math.floor(rating);
    const decimal = rating - floor;
    const needsHalf = decimal >= 0.3 && decimal < 0.75;
    let html = '';

    if (needsHalf) {
      html += `<svg width="0" height="0" style="position:absolute;pointer-events:none">
        <defs>
          <linearGradient id="half-grad-${uid}" x1="0" x2="100%" y1="0" y2="0">
            <stop offset="50%" stop-color="${ACCENT}"/>
            <stop offset="50%" stop-color="${ACCENT_MUTE}"/>
          </linearGradient>
        </defs>
      </svg>`;
    }

    for (let i = 1; i <= 5; i++) {
      let fill;
      if (i <= floor)                                        fill = ACCENT;
      else if (i === floor + 1 && decimal >= 0.75)           fill = ACCENT;
      else if (i === floor + 1 && needsHalf)                 fill = `url(#half-grad-${uid})`;
      else                                                   fill = ACCENT_MUTE;

      html += `<svg width="22" height="22" viewBox="0 0 32 32" class="oke-brand-star" aria-hidden="true">
        <path fill="${fill}" d="M31.547 12a.848.848 0 00-.677-.577l-9.427-1.376-4.224-8.532a.847.847 0 00-1.516 0l-4.218 8.534-9.427 1.355a.847.847 0 00-.467 1.467l6.823 6.664-1.612 9.375a.847.847 0 001.23.893l8.428-4.434 8.432 4.432a.847.847 0 001.229-.894l-1.615-9.373 6.822-6.665a.845.845 0 00.214-.869z"/>
      </svg>`;
    }
    return html;
  }

  /* ── Parse Okendo JSON blob ──────────────────────────────── */
  function getOkeData(widget) {
    const blob = widget.querySelector('script[data-oke-metafield-data]');
    if (!blob) return null;
    try { return JSON.parse(blob.textContent.trim()); }
    catch (e) { console.warn('[invizi-okendo] JSON parse error', e); return null; }
  }

  /* ── Build score panel element ───────────────────────────── */
  function buildScorePanel(agg) {
    const rating    = agg.ratingAndReviewCount > 0
      ? agg.ratingAndReviewValuesTotal / agg.ratingAndReviewCount
      : 0;
    const ratingStr = (Math.round(rating * 10) / 10).toFixed(1);
    const countStr  = agg.ratingAndReviewCount.toLocaleString();
    const uid       = Math.random().toString(36).slice(2, 8);

    const el = document.createElement('div');
    el.className = 'oke-brand-scorePanel';
    el.setAttribute('aria-label', `Rated ${ratingStr} out of 5. ${countStr} reviews.`);
    el.innerHTML = `
      <div class="oke-brand-starsRow">${buildStars(rating, uid)}</div>
      <div class="oke-brand-scoreNumber">
        <span class="oke-brand-score">${ratingStr}</span>
        <span class="oke-brand-scoreMax">/5</span>
      </div>
      <div class="oke-brand-reviewCount">${countStr} reviews</div>
    `;
    return el;
  }

  /* ── Build CTA button element ────────────────────────────── */
  function buildCtaSlot(widget) {
    const originalBtn = widget.querySelector(
      '.oke-w-reviews-writeReview a, .oke-w-reviews-writeReview button'
    );

    // Locale strategy:
    //   1. Prefer Okendo's own button text (it ships localized via the Okendo
    //      app's locale settings) when present — clone the original button
    //      and KEEP its text, just restyle it.
    //   2. If Okendo hasn't rendered yet, fall back to a label provided by
    //      the theme via data-write-review-label (set from the
    //      'products.product.write_review' translation key).
    //   3. Final fallback: English.
    const themeLabel =
      (widget.getAttribute('data-write-review-label') || '').trim();
    const fallbackLabel = themeLabel || 'Write a Review';

    const slot = document.createElement('div');
    slot.className = 'oke-brand-ctaSlot';

    if (originalBtn) {
      const clone = originalBtn.cloneNode(true);
      clone.className = 'oke-brand-writeReviewBtn';
      // Only override the text if Okendo's button is empty for some reason —
      // otherwise keep Okendo's localized label.
      const existingText = (clone.textContent || '').trim();
      if (!existingText) {
        clone.textContent = fallbackLabel;
      }
      slot.appendChild(clone);
    } else {
      const a = document.createElement('a');
      a.className = 'oke-brand-writeReviewBtn';
      a.href = '#';
      a.textContent = fallbackLabel;
      slot.appendChild(a);
    }

    return slot;
  }

  /* ── Inject into header content ──────────────────────────── */
  function inject(widget) {
    const data = getOkeData(widget);
    if (!data?.reviewAggregate) return false;

    // Target: the first .oke-w-header-content-module--left inside the header
    const targetModule = widget.querySelector(
      '.oke-w-header .oke-w-header-content-module.oke-w-header-content-module--left'
    );
    if (!targetModule) return false;

    // Need the write-review btn to be present so we can clone its href
    const writeBtn = widget.querySelector(
      '.oke-w-reviews-writeReview a, .oke-w-reviews-writeReview button'
    );
    if (!writeBtn) return false; // not ready yet — return false to retry

    // ── Score panel: prepend as first child of the module ────
    if (!targetModule.querySelector('.oke-brand-scorePanel')) {
      targetModule.insertBefore(buildScorePanel(data.reviewAggregate), targetModule.firstChild);
    }

    // ── CTA slot: append as last child of the module ─────────
    if (!targetModule.querySelector('.oke-brand-ctaSlot')) {
      targetModule.appendChild(buildCtaSlot(widget));
    }

    // ── Hide original write-review row ───────────────────────
    const origWrap = widget.querySelector('.oke-w-reviews-writeReview');
    if (origWrap) origWrap.style.display = 'none';

    return true; // success
  }

  /* ── Strip trailing commas from tag value spans ─────────── */
  function stripCommas(root) {
    root.querySelectorAll('.oke-w-selectAttr-item-value span').forEach((span) => {
      span.childNodes.forEach((node) => {
        if (node.nodeType === Node.TEXT_NODE) {
          node.textContent = node.textContent.replace(/,\s*$/, '').trim();
        }
      });
    });
  }

  /* ── Reorder individual review cards ────────────────────── */
  /*
   *  Target order per card:
   *    1. Stars          (.oke-reviewContent-stars)
   *    2. Title          (.oke-reviewContent-title)
   *    3. Reviewer info  (.oke-w-reviewMinimal-head)
   *    4. Tag attributes (.oke-w-reviewMinimal-reviewerAttributes)
   *    5. Body text      (.oke-reviewContent-body)
   *    6. Footer         (.oke-w-reviewMinimal-footer)
   */
  function reorderReview(item) {
    if (item.dataset.inviziBrandReordered) return;
    item.dataset.inviziBrandReordered = 'true';

    const stars    = item.querySelector('.oke-reviewContent-stars');
    const title    = item.querySelector('.oke-reviewContent-title');
    const head     = item.querySelector('.oke-w-reviewMinimal-head');
    const attrs    = item.querySelector('.oke-w-reviewMinimal-reviewerAttributes');
    const body     = item.querySelector('.oke-reviewContent-body');
    const footer   = item.querySelector('.oke-w-reviewMinimal-footer');

    // Need at least stars + title to proceed
    if (!stars || !title) return;

    // Build a new wrapper to hold the reordered content
    const wrapper = document.createElement('div');
    wrapper.className = 'oke-brand-reviewCard';

    const append = (el) => { if (el) wrapper.appendChild(el); };

    append(stars);
    append(title);
    append(head);
    append(attrs);
    append(body);
    append(footer);

    // Clear the item and replace with reordered wrapper
    item.innerHTML = '';
    item.appendChild(wrapper);

    // Strip commas from tag spans in this card
    stripCommas(item);
  }

  function reorderAllReviews(widget) {
    widget.querySelectorAll('.oke-w-reviews-list-item').forEach(reorderReview);
  }

  /* ── Watch for new review items loaded via Show More ─────── */
  function watchReviewList(widget) {
    const list = widget.querySelector('.oke-w-reviews-list');
    if (!list || list.dataset.inviziBrandListWatched) return;
    list.dataset.inviziBrandListWatched = 'true';

    const mo = new MutationObserver(() => {
      widget.querySelectorAll('.oke-w-reviews-list-item:not([data-invizi-brand-reordered])').forEach(reorderReview);
    });
    mo.observe(list, { childList: true });
  }

  /* ── Watch a widget's header for Okendo re-renders ──────── */
  function watchHeader(widget) {
    const header = widget.querySelector('.oke-w-header');
    if (!header || header.dataset.inviziBrandWatched) return;
    header.dataset.inviziBrandWatched = 'true';

    const mo = new MutationObserver(() => {
      const targetModule = header.querySelector(
        '.oke-w-header-content-module.oke-w-header-content-module--left'
      );
      if (!targetModule) return;

      // If our score panel is gone → Okendo re-rendered → re-inject
      if (!targetModule.querySelector('.oke-brand-scorePanel')) {
        // Small debounce so Okendo finishes its own DOM work first
        clearTimeout(header._inviziBrandTimer);
        header._inviziBrandTimer = setTimeout(() => {
          inject(widget);
        }, 80);
      }
    });

    mo.observe(header, { childList: true, subtree: true });
  }


  /* ── Per-widget setup ────────────────────────────────────── */
  function setupWidget(widget) {
    // Attempt header inject; if not ready yet the poll will retry
    const ok = inject(widget);
    if (ok) {
      watchHeader(widget);
    }
    // Reorder any review cards already in the DOM
    reorderAllReviews(widget);
    // Watch for more reviews loaded via "Show More"
    watchReviewList(widget);
  }

  /* ── Poll until widget is ready ──────────────────────────── */
  function poll() {
    let elapsed = 0;
    const timer = setInterval(() => {
      elapsed += POLL_MS;

      document.querySelectorAll('[data-oke-widget][data-oke-rendered]').forEach((widget) => {
        const targetModule = widget.querySelector(
          '.oke-w-header .oke-w-header-content-module.oke-w-header-content-module--left'
        );
        if (!targetModule) return;

        // Re-run setup if our panel is missing (covers both first-run and re-renders)
        if (!targetModule.querySelector('.oke-brand-scorePanel')) {
          setupWidget(widget);
        }
      });

      if (elapsed >= POLL_MAX) {
        clearInterval(timer);
      }
    }, POLL_MS);
  }

  /* ── Global observer for dynamically added widgets ───────── */
  function observeGlobal() {
    const mo = new MutationObserver(() => {
      document.querySelectorAll('[data-oke-widget][data-oke-rendered]').forEach((widget) => {
        const targetModule = widget.querySelector(
          '.oke-w-header .oke-w-header-content-module.oke-w-header-content-module--left'
        );
        if (targetModule && !targetModule.querySelector('.oke-brand-scorePanel')) {
          setupWidget(widget);
        }
      });
    });

    mo.observe(document.body, {
      childList:       true,
      subtree:         true,
      attributes:      true,
      attributeFilter: ['data-oke-rendered'],
    });
  }

  /* ── Store reviews badge count template ──────────────────── */
  function applyStoreReviewsTemplate(root) {
    const badgeWrapper = root.querySelector('[data-store-reviews-badge-wrapper]');
    if (!badgeWrapper) return false;

    const template = (badgeWrapper.dataset.storeReviewsCountTemplate || '').trim();
    if (!template.includes('[review_count]')) return false;

    const countElement = root.querySelector('.oke-smallBadge-count');
    if (!countElement) return false;

    const sourceText = countElement.textContent || '';
    const countMatch = sourceText.match(/[\d.,]+/);
    const reviewCount = countMatch ? countMatch[0] : '0';
    const templateParts = template.split('[review_count]');

    if (templateParts.length < 2) return false;

    const prefix = templateParts[0].trim();
    const suffix = templateParts.slice(1).join('[review_count]').trim();

    countElement.textContent = '';

    if (prefix) {
      const prefixElement = document.createElement('span');
      prefixElement.className = 'store-reviews__count-prefix';
      prefixElement.textContent = prefix;
      countElement.append(prefixElement);
    }

    const valueElement = document.createElement('span');
    valueElement.className = 'store-reviews__count-value';
    valueElement.textContent = reviewCount;
    countElement.append(valueElement);

    if (suffix) {
      const suffixElement = document.createElement('span');
      suffixElement.className = 'store-reviews__count-suffix';
      suffixElement.textContent = suffix;
      countElement.append(suffixElement);
    }

    return true;
  }

  function initStoreReviewsTemplate(root) {
    const targets = root.querySelectorAll('[data-store-reviews]');

    targets.forEach((target) => {
      if (target.dataset.storeReviewsTemplateInitialized === 'true') return;
      target.dataset.storeReviewsTemplateInitialized = 'true';

      if (applyStoreReviewsTemplate(target)) return;

      const observer = new MutationObserver(() => {
        if (applyStoreReviewsTemplate(target)) {
          observer.disconnect();
          delete target._storeReviewsTemplateObserver;
        }
      });

      target._storeReviewsTemplateObserver = observer;
      observer.observe(target, {
        childList: true,
        subtree: true,
        characterData: true,
      });

      setTimeout(() => {
        if (target._storeReviewsTemplateObserver) {
          target._storeReviewsTemplateObserver.disconnect();
          delete target._storeReviewsTemplateObserver;
        }
      }, 8000);
    });
  }

  function setupStoreReviews() {
    initStoreReviewsTemplate(document);

    document.addEventListener('shopify:section:load', (event) => {
      const section = event?.target;
      if (!(section instanceof Element)) return;
      initStoreReviewsTemplate(section);
    });
  }

  /* ── Boot ────────────────────────────────────────────────── */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      poll();
      observeGlobal();
      setupStoreReviews();
    });
  } else {
    poll();
    observeGlobal();
    setupStoreReviews();
  }

})();
